import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MoonStar } from "lucide-react";
import { LogoMark } from "./Logo";
import { useLang } from "../i18n";
import { isOpenNow } from "../siteData";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export function Header() {
    const { lang, setLang, t } = useLang();
    const [open, setOpen] = useState(isOpenNow());

    useEffect(() => {
        const iv = setInterval(() => setOpen(isOpenNow()), 60000);
        return () => clearInterval(iv);
    }, []);

    const links = [
        { id: "story", label: t.nav.story, testid: "nav-link-story" },
        { id: "services", label: t.nav.services, testid: "nav-link-services" },
        { id: "gallery", label: t.nav.gallery, testid: "nav-link-gallery" },
        { id: "booking", label: t.nav.booking, testid: "nav-link-booking" },
    ];

    return (
        <header className="fixed top-0 inset-x-0 z-50">
            <div className="mx-auto max-w-7xl px-4 sm:px-6">
                <div className="mt-4 flex items-center justify-between rounded-2xl border border-white/10 bg-black/60 px-4 py-2.5 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.5)]">
                    <button
                        data-testid="header-logo"
                        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        className="flex items-center gap-3"
                    >
                        <LogoMark size={36} />
                        <span className="leading-none text-left">
                            <span className="block font-display font-extrabold tracking-wide text-[#F4F4F6] text-sm sm:text-base">
                                9CUT <span className="text-[#FFB800]">SALON</span>
                            </span>
                            <span className="block font-mono2 text-[10px] tracking-[0.3em] text-[#8E8EA0] uppercase">
                                {t.brandSub}
                            </span>
                        </span>
                    </button>

                    <nav className="hidden lg:flex items-center gap-7">
                        {links.map((l) => (
                            <button
                                key={l.id}
                                data-testid={l.testid}
                                onClick={() => scrollTo(l.id)}
                                className="text-sm text-[#8E8EA0] hover:text-[#FFB800] transition-colors duration-300"
                            >
                                {l.label}
                            </button>
                        ))}
                        <Link
                            to="/admin"
                            data-testid="nav-link-admin"
                            className="text-sm text-[#8E8EA0] hover:text-[#FFB800] transition-colors duration-300"
                        >
                            {t.nav.admin}
                        </Link>
                    </nav>

                    <div className="flex items-center gap-2 sm:gap-3">
                        <span
                            data-testid="open-status-badge"
                            className={`hidden md:inline-flex items-center gap-2 rounded-full border px-3 py-1 font-mono2 text-[10px] tracking-widest ${
                                open
                                    ? "border-[#FF2E4D]/40 text-[#FF2E4D]"
                                    : "border-white/10 text-[#8E8EA0]"
                            }`}
                        >
                            <span className={`h-1.5 w-1.5 rounded-full ${open ? "bg-[#FF2E4D] pulse-dot" : "bg-[#8E8EA0]"}`} />
                            {open ? t.status.open : t.status.closed} · {t.status.hours}
                        </span>
                        <button
                            data-testid="language-toggle-btn"
                            onClick={() => setLang(lang === "zh" ? "en" : "zh")}
                            className="rounded-full border border-white/15 px-3 py-1.5 font-mono2 text-xs text-[#F4F4F6] hover:border-[#FFB800] hover:text-[#FFB800] transition-colors duration-300"
                        >
                            {lang === "zh" ? "EN" : "中文"}
                        </button>
                        <button
                            data-testid="header-book-btn"
                            onClick={() => scrollTo("booking")}
                            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#FFB800] px-4 py-2 text-sm font-semibold text-[#0B0B0E] hover:bg-[#FF2E4D] hover:text-white transition-colors duration-300"
                        >
                            <MoonStar size={15} />
                            {t.nav.booking}
                        </button>
                    </div>
                </div>
            </div>
        </header>
    );
}
